// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * hex.ts — axial hex coordinates for a BOARDLESS field.
 *
 * There is no grid array, because there is no grid: the colony is a sparse map
 * from `"q,r"` to a piece, and it may grow in any direction forever. Everything
 * geometric the rules need lives here, and none of it touches game state.
 */

export interface Hex {
  q: number;
  r: number;
}

export type HexKey = string;

/** Pointy-top axial neighbour offsets, in clockwise order starting E. */
export const DIRS: readonly Hex[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export const key = (h: Hex): HexKey => `${h.q},${h.r}`;

export const parseKey = (k: HexKey): Hex => {
  const i = k.indexOf(',');
  return { q: Number(k.slice(0, i)), r: Number(k.slice(i + 1)) };
};

export const eq = (a: Hex, b: Hex): boolean => a.q === b.q && a.r === b.r;

export const add = (a: Hex, b: Hex): Hex => ({ q: a.q + b.q, r: a.r + b.r });

export const neighbors = (h: Hex): Hex[] => DIRS.map((d) => add(h, d));

/** Neighbour keys, which is what the rules actually index by. */
export const neighborKeys = (h: Hex): HexKey[] => DIRS.map((d) => key(add(h, d)));

/**
 * The two cells adjacent to BOTH `a` and `b`, when `a` and `b` are themselves
 * adjacent. This pair is the whole of the slide rule: one of them must be
 * occupied (you keep contact with the colony) and — unless the piece burrows —
 * one must be empty (you can physically squeeze between them).
 */
export function gateCells(a: Hex, b: Hex): [Hex, Hex] {
  const i = DIRS.findIndex((d) => a.q + d.q === b.q && a.r + d.r === b.r);
  if (i < 0) throw new Error('gateCells: cells are not adjacent');
  return [add(a, DIRS[(i + 5) % 6]), add(a, DIRS[(i + 1) % 6])];
}

/** Axial distance — used only by the AI's heuristics, never by the rules. */
export function distance(a: Hex, b: Hex): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

/**
 * Is the set of occupied cells one single connected cluster?
 *
 * The one-colony rule is checked on every candidate move, so this runs in the
 * innermost loop of both the move generator and the AI search. A colony is at
 * most ~20 cells, so a plain BFS over a Set is comfortably the right shape.
 */
export function isConnected(occupied: Set<HexKey>): boolean {
  if (occupied.size <= 1) return true;
  const start = occupied.values().next().value as HexKey;
  const seen = new Set<HexKey>([start]);
  const stack: HexKey[] = [start];
  while (stack.length) {
    const cur = parseKey(stack.pop()!);
    for (const nk of neighborKeys(cur)) {
      if (occupied.has(nk) && !seen.has(nk)) {
        seen.add(nk);
        stack.push(nk);
      }
    }
  }
  return seen.size === occupied.size;
}

/**
 * Every cell whose removal would split the colony ("you may not lift this").
 *
 * Computed once per turn rather than per candidate move: the naive form is
 * `for each piece { remove it; isConnected() }`, which is O(n²) and was the hot
 * spot in the balance sim. This is Hopcroft–Tarjan articulation points in one
 * iterative DFS, O(n).
 */
export function cutCells(occupied: Set<HexKey>): Set<HexKey> {
  const cuts = new Set<HexKey>();
  if (occupied.size <= 2) return cuts;

  const disc = new Map<HexKey, number>();
  const low = new Map<HexKey, number>();
  const parent = new Map<HexKey, HexKey | null>();
  let timer = 0;

  const root = occupied.values().next().value as HexKey;
  let rootChildren = 0;

  // Iterative DFS: (cell, index of the next neighbour to examine).
  const stack: Array<{ k: HexKey; i: number }> = [{ k: root, i: 0 }];
  disc.set(root, timer);
  low.set(root, timer);
  timer++;
  parent.set(root, null);

  while (stack.length) {
    const frame = stack[stack.length - 1];
    const cur = parseKey(frame.k);
    if (frame.i < 6) {
      const nk = key(add(cur, DIRS[frame.i]));
      frame.i++;
      if (!occupied.has(nk)) continue;
      if (!disc.has(nk)) {
        parent.set(nk, frame.k);
        disc.set(nk, timer);
        low.set(nk, timer);
        timer++;
        if (frame.k === root) rootChildren++;
        stack.push({ k: nk, i: 0 });
      } else if (nk !== parent.get(frame.k)) {
        low.set(frame.k, Math.min(low.get(frame.k)!, disc.get(nk)!));
      }
    } else {
      stack.pop();
      const p = parent.get(frame.k);
      if (p != null) {
        low.set(p, Math.min(low.get(p)!, low.get(frame.k)!));
        if (p !== root && low.get(frame.k)! >= disc.get(p)!) cuts.add(p);
      }
    }
  }

  if (rootChildren > 1) cuts.add(root);
  return cuts;
}

/** Pixel centre of a hex for a pointy-top layout of the given size. */
export function toPixel(h: Hex, size: number): { x: number; y: number } {
  return {
    x: size * Math.sqrt(3) * (h.q + h.r / 2),
    y: size * 1.5 * h.r,
  };
}

/** Inverse of `toPixel`, rounded to the nearest hex. */
export function fromPixel(x: number, y: number, size: number): Hex {
  const r = (2 / 3) * (y / size);
  const q = x / (size * Math.sqrt(3)) - r / 2;
  return roundHex(q, r);
}

function roundHex(qf: number, rf: number): Hex {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q, r };
}
